import { useEffect, useMemo, useState } from 'react'

import { ChartLevels, type Level } from '@/components/ChartLevels'
import { ChartDrawings } from '@/components/ChartDrawings'
import { ChartIndicators } from '@/components/ChartIndicators'
import { ChartOrderButton } from '@/components/ChartOrderButton'
import { DrawingTools } from '@/components/DrawingTools'
import { IndicatorTools } from '@/components/IndicatorTools'
import { PriceChart, type ChartApi } from '@/components/PriceChart'
import { QuoteTicker } from '@/components/QuoteTicker'
import { SizingControl } from '@/components/SizingControl'
import { StaleQuotes } from '@/components/StaleQuotes'
import { Card, Segmented } from '@/components/ui'
import { useIndicators } from '@/hooks/useIndicators'
import { useLocalState } from '@/hooks/useLocalState'
import type { HeldSymbol } from '@/lib/positions'
import {
  DRAWING_COLORS,
  loadDrawings,
  saveDrawings,
  type DrawingKind,
} from '@/lib/drawings'
import { api } from '@/lib/api'
import { generateDemoCandles } from '@/lib/demoData'
import { foldQuote } from '@/lib/liveCandles'
import {
  CANDLE_COUNT,
  TIMEFRAMES,
  type Candle,
  type Instrument,
  type LiveQuote,
  type PlanRequest,
  type PlanResponse,
  type Side,
  type SizingMode,
  type Timeframe,
} from '@/lib/types'

/**
 * Graphique et données de marché.
 *
 * Trois situations, toutes explicites à l'écran :
 *
 * - **broker connecté** : chandeliers et cotations réels ;
 * - **broker absent** : message clair, et proposition d'activer une
 *   démonstration ;
 * - **démonstration** : bandeau permanent, impossible à confondre avec du réel.
 */
export function MarketPanel({
  paneId = 'main',
  symbol,
  instrument,
  quote,
  held,
  quoteAge = null,
  brokerReady,
  side,
  stopLossPips,
  takeProfitPips,
  riskAmount,
  rewardAmount,
  currency,
  onChangeStop,
  onChangeTarget,
  onChangeSide,
  maxRewardRatio = 2,
  orderRefusal,
  request,
  plan,
  chartHeight = 300,
  showLevels = true,
  fullscreen = false,
  onToggleFullscreen,
  onClose,
  onPickSymbol,
  lots,
  onChangeLots,
  sizingMode,
  onChangeSizingMode,
  riskAmountInput,
  onChangeRiskAmount,
  riskPercentInput,
  onChangeRiskPercent,
}: {
  /** Identifiant du panneau : chaque graphique garde son propre intervalle. */
  paneId?: string
  symbol: string
  instrument: Instrument | undefined
  quote: LiveQuote | null
  /** Secondes depuis la dernière cotation reçue par le serveur. */
  quoteAge?: number | null
  brokerReady: boolean
  side: 'BUY' | 'SELL'
  stopLossPips: string
  takeProfitPips: string
  riskAmount: string | null
  rewardAmount: string | null
  currency: string
  onChangeStop: (pips: string) => void
  onChangeTarget: (pips: string) => void
  /**
   * Bascule le sens de l'ordre.
   *
   * Appelé quand un niveau passe de l'autre côté du prix d'entrée : c'est la
   * position du stop qui décide du sens, pas l'inverse.
   */
  onChangeSide?: (side: Side) => void
  /**
   * Ratio gain/risque maximum de la saisie.
   *
   * Le glissement est BORNÉ par cette valeur : on ne peut pas poser une
   * cible à plus de N fois son stop. C'est ce plafond qui borne
   * l'exposition réelle — l'ordre partant inversé, la perte possible sur le
   * compte vaut le risque saisi multiplié par ce ratio. Le serveur le
   * vérifie de toute façon ; ici, on empêche simplement le geste plutôt
   * que de laisser composer un ordre refusé.
   */
  maxRewardRatio?: number
  /** Refus calculé par l'écran, transmis au bouton d'ordre. */
  orderRefusal?: string | null
  /** Ordre en préparation, pour le bouton posé sous le graphique. */
  request?: PlanRequest | null
  plan?: PlanResponse | null
  /** Hauteur du graphique en pixels. */
  chartHeight?: number
  /** Affiche les niveaux déplaçables. Faux sur les graphiques secondaires. */
  showLevels?: boolean
  /** Bouton plein écran, si le parent sait le gérer. */
  fullscreen?: boolean
  onToggleFullscreen?: () => void
  /** Bouton de fermeture du panneau, dans une grille. */
  onClose?: () => void
  /** Ouvre le sélecteur de paire pour CE panneau. */
  onPickSymbol?: () => void
  /** Positions déjà ouvertes sur la paire de ce panneau. */
  held?: HeldSymbol
  /** Volume courant, ajustable depuis le graphique. */
  lots?: string
  onChangeLots?: (lots: string) => void
  /**
   * Dimensionnement, réglable sans quitter le graphique.
   *
   * C'est ici qu'on décide de la taille : descendre au ticket pour passer de
   * « 0,386 lot » à « 1 % » casse le geste au moment où l'on regarde le prix.
   */
  sizingMode?: SizingMode
  onChangeSizingMode?: (mode: SizingMode) => void
  riskAmountInput?: string
  onChangeRiskAmount?: (amount: string) => void
  riskPercentInput?: string
  onChangeRiskPercent?: (percent: string) => void
}) {
  const [timeframe, setTimeframe] = useLocalState<Timeframe>(`timeframe.${paneId}`, 'M15')
  // Le mode démonstration reste global : il décrit la source des données, pas
  // la préférence d'un panneau.
  const [demo, setDemo] = useLocalState('demoChart', false)
  const [candles, setCandles] = useState<Candle[]>([])
  const [error, setError] = useState<string | null>(null)
  const [loading, setLoading] = useState(false)

  const digits = instrument?.digits ?? 5
  const pipSize = Number(instrument?.pip_size ?? 0.0001)
  const [chartApi, setChartApi] = useState<ChartApi | null>(null)

  // Outils de dessin. L'outil armé n'est PAS mémorisé d'une session à l'autre :
  // retrouver le mode « ligne » au chargement ferait poser un trait au premier
  // geste de défilement. La couleur, elle, se retient.
  const [toolsOpen, setToolsOpen] = useState(false)
  const [tool, setTool] = useState<DrawingKind | null>(null)
  const [drawingColor, setDrawingColor] = useLocalState<string>(
    'drawingColor',
    DRAWING_COLORS[0],
  )
  const [drawingCount, setDrawingCount] = useState(0)

  /*
   * Indicateurs : moyennes mobiles et bandes de Bollinger.
   *
   * La liste est commune à tous les graphiques ouverts — c'est un réglage,
   * pas une annotation de paire. Le panneau, lui, s'ouvre panneau par
   * panneau : on ne règle qu'un graphique à la fois.
   */
  const [indicators, setIndicators] = useIndicators()
  const [indicatorsOpen, setIndicatorsOpen] = useState(false)

  // Le compteur alimente la barre d'outils ; il est relu à chaque changement
  // de paire et après chaque tracé.
  useEffect(() => {
    setDrawingCount(loadDrawings(symbol).length)
  }, [symbol, tool])

  // Prix de référence des niveaux : la cotation en direct si le broker est
  // connecté, sinon la dernière clôture affichée. Sans prix, pas de niveaux —
  // on ne montre pas de lignes posées sur une valeur inventée.
  const entryPrice = useMemo(() => {
    // En démonstration, la cotation en direct n'a rien à voir avec les bougies
    // engendrées localement : s'en servir plaçait les niveaux à des centaines
    // de pips du graphique, hors du cadre et impossibles à attraper.
    if (!demo && quote) return Number(quote.mid)
    const last = candles.at(-1)
    return last ? Number(last.close) : null
  }, [demo, quote, candles])

  /**
   * Les deux niveaux, avec leur double rôle.
   *
   * Le stop que je saisis devient la cible de l'ordre envoyé, et ma cible
   * devient son stop.
   *
   * **Les couleurs suivent MA lecture** : rouge sur mon stop, vert sur ma
   * cible, comme sur n'importe quelle plateforme. Elles suivaient auparavant
   * l'ordre réellement envoyé — vert là où le compte gagne — ce qui était
   * exact mais se lisait à l'envers : une ligne verte sous le prix d'entrée
   * évoque spontanément une vente. C'est aussi la convention qu'emploie déjà
   * l'éditeur de position ; les deux écrans se lisent maintenant pareil.
   *
   * L'argent réel n'est pas perdu de vue pour autant : chaque étiquette porte
   * le montant SIGNÉ et le rôle du niveau chez le courtier. Sur mon stop,
   * rouge, on lit donc « +1,97 EUR (cible du courtier) » — la couleur dit ce
   * que je risque, le texte dit ce que le compte fait.
   */
  const levels = useMemo<Level[]>(() => {
    if (entryPrice === null) return []

    const stop = Number(stopLossPips)
    const target = Number(takeProfitPips)
    const buy = side === 'BUY'
    const result: Level[] = []

    if (Number.isFinite(stop) && stop > 0) {
      result.push({
        id: 'stop',
        // Pour un achat saisi, le stop est sous le prix d'entrée.
        price: buy ? entryPrice - stop * pipSize : entryPrice + stop * pipSize,
        pips: stop,
        // « MON STOP » et non « SL saisi » : c'est CETTE ligne qui décide du
        // sens, et le mot « stop » ne doit apparaître ailleurs qu'attribué au
        // courtier — sans quoi on lit le stop du courtier comme le sien.
        label: 'MON STOP',
        mirrorLabel: 'cible du courtier',
        color: '#ef5350',
        amount: rewardAmount ? `+${rewardAmount} ${currency}` : null,
        isRealStop: false,
      })
    }

    if (Number.isFinite(target) && target > 0) {
      result.push({
        id: 'target',
        price: buy ? entryPrice + target * pipSize : entryPrice - target * pipSize,
        pips: target,
        label: 'MA CIBLE',
        // Quand la cible bute sur le plafond, la ligne cesse de suivre le
        // doigt : sans un mot, on croirait à un bug.
        mirrorLabel:
          Number.isFinite(stop) && stop > 0 && target >= stop * maxRewardRatio - 0.05
            ? `stop du courtier · plafond 1:${maxRewardRatio}`
            : 'stop du courtier',
        color: '#26a69a',
        amount: riskAmount ? `−${riskAmount} ${currency}` : null,
        isRealStop: true,
      })
    }

    return result
  }, [entryPrice, stopLossPips, takeProfitPips, side, pipSize, riskAmount, rewardAmount, currency, maxRewardRatio])

  useEffect(() => {
    let cancelled = false

    if (demo) {
      setCandles(generateDemoCandles(symbol, timeframe, CANDLE_COUNT[timeframe]))
      setError(null)
      return
    }

    if (!brokerReady) {
      setCandles([])
      setError(null)
      return
    }

    /*
     * On VIDE les bougies avant d'aller chercher les nouvelles.
     *
     * Sans cela, celles de la paire précédente restaient affichées pendant la
     * requête, et le prix de référence des niveaux était pris sur elles quand
     * la nouvelle paire n'avait pas encore de cotation. Résultat : des lignes
     * posées à 0,65 sur un graphique qui cote 1,16. L'échelle s'élargissait
     * pour les inclure et écrasait les bougies en un trait invisible — d'où
     * une paire qui « n'affiche que le stop et le take profit ».
     */
    setCandles([])
    setLoading(true)

    const load = (initial: boolean) =>
      api
        .trendbars(symbol, timeframe, CANDLE_COUNT[timeframe])
        .then((data) => {
          if (cancelled) return
          setCandles(data)
          setError(null)
        })
        .catch((err: unknown) => {
          if (cancelled) return
          // Un rafraîchissement de fond qui échoue garde ce qu'on a : effacer
          // le graphique pour une requête ratée serait pire que de le laisser
          // quelques minutes sans correction.
          if (!initial) return
          setCandles([])
          setError(err instanceof Error ? err.message : 'Erreur inconnue')
        })
        .finally(() => {
          if (!cancelled && initial) setLoading(false)
        })

    void load(true)

    /*
     * Rechargement de fond, SANS vider le graphique.
     *
     * La dernière bougie vit grâce aux cotations, mais celles-ci ne disent
     * rien de ce qui s'est passé pendant qu'on ne regardait pas : un téléphone
     * mis en veille une heure revient avec un trou d'une heure. On recharge
     * donc au retour au premier plan, et toutes les cinq minutes, pour que le
     * courtier corrige ce que le repli local aurait approximé.
     */
    const refresh = () => {
      if (document.visibilityState === 'visible') void load(false)
    }
    const timer = window.setInterval(refresh, 5 * 60_000)
    document.addEventListener('visibilitychange', refresh)

    return () => {
      cancelled = true
      window.clearInterval(timer)
      document.removeEventListener('visibilitychange', refresh)
    }
  }, [symbol, timeframe, brokerReady, demo])

  // Chaque cotation fait vivre la dernière bougie. Déclaré APRÈS le
  // chargement : au changement de paire, le vidage passe en premier, et le
  // repli trouve alors un tableau vide au lieu des bougies de l'ancienne paire.
  /*
   * La démonstration ne sert qu'EN L'ABSENCE de courtier : c'est d'ailleurs le
   * seul état où l'on peut y entrer. Dès que le courtier répond, on revient
   * aux vrais prix.
   *
   * Ce mode se conserve d'une session à l'autre. Laissé allumé, il affichait
   * des bougies factices au-dessus d'un compte bel et bien connecté, et
   * l'ordre était refusé avec un message parlant de conversion de devises :
   * faute de cotation réelle, rien n'était convertible. En plein écran, le
   * lien « Quitter la démonstration » n'apparaît pas — il n'y avait donc
   * aucune issue visible.
   */
  useEffect(() => {
    if (demo && brokerReady) setDemo(false)
  }, [demo, brokerReady, setDemo])

  useEffect(() => {
    if (demo || !quote || quote.symbol !== symbol) return
    const price = Number(quote.mid)
    setCandles((current) => foldQuote(current, price, timeframe, Date.now() / 1000))
  }, [quote, symbol, timeframe, demo])

  /*
   * Raccourcis clavier — l'usage d'une plateforme à la souris.
   *
   * 1 à 7 changent l'intervalle, F bascule le plein écran, S ouvre la liste
   * des paires. Seul le panneau qui porte le ticket répond : avec quatre
   * graphiques à l'écran, une touche en changerait quatre à la fois.
   *
   * Rien ne se déclenche pendant une saisie, ni quand une feuille est ouverte
   * — elle a ses propres touches. Aucun raccourci n'envoie d'ordre : une
   * frappe malheureuse ne doit jamais ouvrir une position.
   */
  useEffect(() => {
    if (!showLevels) return undefined

    function onKey(event: KeyboardEvent) {
      if (event.metaKey || event.ctrlKey || event.altKey) return
      const cible = event.target as HTMLElement | null
      if (
        cible &&
        (cible.isContentEditable || ['INPUT', 'TEXTAREA', 'SELECT'].includes(cible.tagName))
      ) {
        return
      }
      if (document.querySelector('[role="dialog"]')) return

      const rang = Number(event.key)
      if (Number.isInteger(rang) && rang >= 1 && rang <= TIMEFRAMES.length) {
        setTimeframe(TIMEFRAMES[rang - 1])
        return
      }
      const touche = event.key.toLowerCase()
      if (touche === 'f' && onToggleFullscreen) {
        onToggleFullscreen()
        return
      }
      if ((touche === 's' || touche === '/') && onPickSymbol) {
        event.preventDefault()
        onPickSymbol()
      }
    }

    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [showLevels, onToggleFullscreen, onPickSymbol, setTimeframe])

  return (
    <Card>
      {demo && (
        <div className="bg-warn/20 px-3 py-1.5 text-center text-[11px] font-semibold text-warn">
          DONNÉES DE DÉMONSTRATION — générées localement, ne correspondent à aucun marché
        </div>
      )}

      <div className="flex items-center justify-between gap-2 px-3 pt-2">
        <button
          type="button"
          onClick={onPickSymbol}
          disabled={!onPickSymbol}
          title={onPickSymbol ? `Changer de paire${showLevels ? ' (S)' : ''}` : undefined}
          className="num min-h-touch shrink-0 text-left text-[15px] font-semibold text-ink-strong"
        >
          {symbol}
          {onPickSymbol && <span className="ml-1 text-[11px] text-ink-muted">▾</span>}
        </button>

        {/* Cotation collée au nom sur téléphone, où chaque bandeau se paie en
            bougies. Sur ordinateur elle garde sa ligne, avec bid et ask. */}
        <div className="min-w-0 flex-1 md:hidden">
          <QuoteTicker quote={demo ? null : quote} digits={digits} compact />
        </div>

        <div className="flex shrink-0 items-center gap-1">
          {instrument && (
            <span className="num mr-1 hidden text-[11px] text-ink-muted md:inline">
              {instrument.digits} déc.
            </span>
          )}
          {onToggleFullscreen && (
            <button
              type="button"
              onClick={onToggleFullscreen}
              aria-label={fullscreen ? 'Quitter le plein écran' : 'Plein écran'}
              title={`${fullscreen ? 'Quitter le plein écran' : 'Plein écran'}${
                showLevels ? ' (F)' : ''
              }`}
              className="min-h-touch w-11 rounded text-[14px] text-ink-muted hover:bg-elevated/70 active:bg-elevated"
            >
              {fullscreen ? '⤡' : '⤢'}
            </button>
          )}
          {onClose && (
            <button
              type="button"
              onClick={onClose}
              aria-label="Fermer ce graphique"
              className="min-h-touch w-11 rounded text-[14px] text-ink-muted hover:bg-elevated/70 active:bg-elevated"
            >
              ✕
            </button>
          )}
        </div>
      </div>

      {/* Prix figés : on le dit, quelle qu'en soit la cause. */}
      {!demo && brokerReady && <StaleQuotes age={quoteAge} />}

      {/* Le sélecteur occupe toute la largeur : sur un écran de 375 px, six
          intervalles partagés avec le nom de la paire rendaient D1 inatteignable.
          Serré, en revanche : pleine hauteur, cette seule barre coûtait près de
          70 px de bougies sur un téléphone, pour un réglage qui se change
          quelques fois par séance. */}
      <div className="border-b border-border px-2 py-1">
        <Segmented<Timeframe>
          dense
          value={timeframe}
          onChange={setTimeframe}
          options={TIMEFRAMES.map((item, rang) => ({
            value: item,
            label: item,
            hint: showLevels ? `Touche ${rang + 1}` : undefined,
          }))}
        />
      </div>

      <div className="hidden md:block">
        <QuoteTicker quote={demo ? null : quote} digits={digits} />
      </div>

      {candles.length > 0 ? (
        <>
        <div className="relative">
          <PriceChart
            candles={candles}
            height={chartHeight}
            digits={digits}
            // Le recadrage automatique ne doit avoir lieu qu'au changement de
            // paire ou d'intervalle, sinon le zoom saute à chaque bougie.
            fitKey={`${symbol}:${timeframe}:${demo ? 'demo' : 'live'}`}
            onReady={setChartApi}
            includePrices={showLevels ? levels.map((level) => level.price) : []}
          >
            {showLevels && (
            <ChartLevels
              api={chartApi}
              entryPrice={entryPrice}
              levels={levels}
              pipSize={pipSize}
              digits={digits}
              height={chartHeight}
              // Court, parce que cette mention partage une ligne de 375 px
              // avec le prix d'entrée. La phrase entière — « mon stop est en
              // dessous » — poussait le tout hors de l'écran, et c'est
              // précisément le sens qui disparaissait.
              direction={side === 'BUY' ? 'ACHAT · stop dessous' : 'VENTE · stop dessus'}
              onDrag={(id, pips, above) => {
                // Le sens de l'ordre se LIT sur le graphique. Un stop posé
                // au-dessus du prix d'entrée n'appartient qu'à une vente ;
                // en dessous, qu'à un achat. La cible dit exactement
                // l'inverse. Faire passer une ligne de l'autre côté de
                // l'entrée bascule donc le sens, et l'autre ligne se replace
                // d'elle-même en miroir.
                const implied: Side =
                  id === 'stop' ? (above ? 'SELL' : 'BUY') : above ? 'BUY' : 'SELL'
                if (implied !== side) onChangeSide?.(implied)

                /*
                 * Plafond du ratio : la cible ne peut pas valoir plus de N
                 * fois le stop. Reste à savoir QUELLE ligne cède quand on
                 * touche la limite.
                 *
                 * Le stop cédait, et c'était le mauvais choix. Avec une cible
                 * à 64 pips et un plafond 1:2, le stop ne descendait plus
                 * sous 32 : la ligne se figeait, le doigt continuait,
                 * franchissait le prix d'entrée — et ce franchissement
                 * basculait le sens. On voulait resserrer un stop, on se
                 * retrouvait acheteur après avoir été vendeur. Sur un H4, où
                 * trente pips font quelques pixels, cela arrivait au moindre
                 * geste.
                 *
                 * **Le niveau qu'on tient va où on le met.** Si cela dépasse
                 * le plafond, c'est l'AUTRE qui se rapproche : resserrer son
                 * stop ramène la cible avec lui, ce qui est de toute façon
                 * ce qu'on veut dire en resserrant un stop. La cible, elle,
                 * reste bornée quand c'est ELLE qu'on écarte — là, le doigt
                 * demande directement l'interdit, et l'étiquette dit pourquoi
                 * la ligne s'arrête.
                 */
                const stop = Number(stopLossPips)
                const target = Number(takeProfitPips)
                // Arrondi vers le BAS, jamais au plus proche : le dixième de
                // pip gagné en arrondissant au plus près suffit à repasser
                // au-dessus du plafond, et le serveur refuse alors un ordre
                // que l'interface venait de présenter comme valide. Sans
                // conséquence avec un plafond entier, fatal avec 1:1,5.
                const cap = (base: number) => Math.floor(base * maxRewardRatio * 10) / 10
                if (id === 'stop') {
                  onChangeStop(String(pips))
                  const ceiling = cap(pips)
                  if (Number.isFinite(target) && target > ceiling) {
                    onChangeTarget(String(ceiling))
                  }
                } else {
                  const ceiling =
                    Number.isFinite(stop) && stop > 0 ? cap(stop) : Infinity
                  onChangeTarget(String(Math.min(pips, ceiling)))
                }
              }}
            />
            )}
            {/*
              Calque de dessin. Il vient APRÈS celui des niveaux SL/TP : les
              tracés se posent par-dessus, mais restent transparents aux gestes
              tant qu'aucun outil n'est armé.
            */}
            {/*
              Les indicateurs ne rendent aucun élément : ils posent des courbes
              DANS le graphique. Ils sont ici pour recevoir `chartApi` au même
              moment que les autres calques.
            */}
            <ChartIndicators api={chartApi} candles={candles} indicators={indicators} />

            <ChartDrawings
              api={chartApi}
              symbol={symbol}
              pipSize={pipSize}
              digits={digits}
              height={chartHeight}
              tool={tool}
              color={drawingColor}
              onFinished={() => {
                setTool(null)
                setDrawingCount(loadDrawings(symbol).length + 1)
              }}
            />
          </PriceChart>
          {demo && (
            // Marquage superposé, visible quelle que soit la position de
            // défilement : le bandeau du haut sort du champ dès qu'on regarde
            // le graphique, et un faux prix non signalé est le pire défaut
            // possible ici.
            // `z-10` : sans lui, le canevas du graphique recouvre le badge.
            // Coin HAUT DROIT : à gauche, il recouvrait l'étiquette du niveau haut
            // sur un écran étroit, et masquait précisément le montant risqué.
            // Cliquable : le badge est le seul repère du mode en plein
            // écran, c'est donc là qu'il faut pouvoir en sortir.
            <button
              type="button"
              onClick={() => setDemo(false)}
              title="Quitter la démonstration"
              className="absolute right-2 top-2 z-10 rounded bg-warn/90 px-2 py-0.5 text-[10px] font-semibold tracking-wide text-[#0b0e14] hover:bg-warn"
            >
              DÉMONSTRATION ✕
            </button>
          )}
        </div>

        {/*
          Barre d'outils SOUS le canevas, et non posée dessus. Tout contrôle
          flottant finit par recouvrir une étiquette de niveau — c'est ce qui
          rendait le réglage du lot illisible dès que le stop descendait.
        */}
        <div className="flex flex-wrap items-center gap-2 border-t border-border px-2 py-2">
          {/*
            Le geste principal passe en PREMIER sur téléphone, et prend la
            ligne entière.

            Il était jusqu'ici le dernier d'une barre qui se replie : sur
            375 px, le dimensionnement, les tracés et l'ajustement occupaient
            déjà deux lignes, et « ACHETER » se retrouvait en troisième
            position, sous la ligne de flottaison. On faisait défiler pour
            trouver le seul bouton qui compte. Il est maintenant collé aux
            bougies, pleine largeur, visible sans un geste. Sur ordinateur, où
            tout tient sur une ligne, il reprend sa place à droite.
          */}
          {showLevels && brokerReady && (
            <div className="order-first flex w-full md:order-last md:w-auto md:flex-1">
              <ChartOrderButton
                side={side}
                held={held}
                refus={orderRefusal ?? null}
                request={request ?? null}
                plan={plan ?? null}
                currency={currency}
                brokerReady={brokerReady}
              />
            </div>
          )}

          {showLevels &&
            lots !== undefined &&
            onChangeLots &&
            instrument &&
            sizingMode &&
            onChangeSizingMode &&
            onChangeRiskAmount &&
            onChangeRiskPercent && (
              <SizingControl
                mode={sizingMode}
                onChangeMode={onChangeSizingMode}
                lots={lots}
                onChangeLots={onChangeLots}
                amount={riskAmountInput ?? ''}
                onChangeAmount={onChangeRiskAmount}
                percent={riskPercentInput ?? ''}
                onChangePercent={onChangeRiskPercent}
                step={instrument.step_lots}
                min={instrument.min_lots}
                max={instrument.max_lots}
                realRisk={riskAmount}
                currency={currency}
              />
            )}

          <IndicatorTools
            open={indicatorsOpen}
            onToggle={() => setIndicatorsOpen((value) => !value)}
            indicators={indicators}
            onChange={setIndicators}
            available={candles.length}
          />

          <DrawingTools
            open={toolsOpen}
            onToggle={() => setToolsOpen((value) => !value)}
            tool={tool}
            onChangeTool={setTool}
            color={drawingColor}
            onChangeColor={setDrawingColor}
            count={drawingCount}
            onClear={() => {
              saveDrawings(symbol, [])
              setDrawingCount(0)
              setTool(null)
            }}
          />

          {/* Retour à la vue d'ensemble : indispensable dès qu'on zoome, sinon
              on se perd dans dix-huit mois d'historique sans repère. */}
          <button
            type="button"
            onClick={() => chartApi?.fit()}
            aria-label="Ajuster le graphique"
            className="min-h-touch rounded-md border border-border bg-elevated px-3 text-[12px] text-ink-muted hover:bg-border/60 active:bg-border"
          >
            ⟲ ajuster
          </button>

        </div>
        </>
      ) : (
        <div className="flex flex-col items-center gap-3 px-4 py-10 text-center">
          {loading ? (
            <p className="text-[13px] text-ink-muted">Chargement des chandeliers…</p>
          ) : error ? (
            <>
              <p className="text-[13px] text-down">{error}</p>
              <p className="text-[12px] text-ink-muted">
                Le mode calculateur reste utilisable sans graphique.
              </p>
            </>
          ) : (
            <>
              <p className="text-[13px] text-ink-muted">
                Broker non connecté : aucune donnée de marché réelle.
              </p>
              <button
                type="button"
                onClick={() => setDemo(true)}
                className="min-h-touch rounded-md border border-border bg-elevated px-4 text-[13px] text-ink hover:bg-border/60 active:bg-border"
              >
                Afficher un graphique de démonstration
              </button>
              <p className="max-w-xs text-[11px] leading-relaxed text-ink-muted">
                Des bougies factices, générées dans le navigateur, pour vérifier
                l'affichage. Elles ne correspondent à aucun prix réel et ne sont
                jamais utilisées par les calculs.
              </p>
            </>
          )}
        </div>
      )}

      {demo && (
        <div className="border-t border-border px-3 py-2 text-center">
          <button
            type="button"
            onClick={() => setDemo(false)}
            className="min-h-touch text-[12px] text-ink-muted underline"
          >
            Quitter la démonstration
          </button>
        </div>
      )}
    </Card>
  )
}
